import React, { useState, FormEvent } from 'react';
import { useTheme } from '../contexts/ThemeProvider';
import { ThemeMode } from '../types/theme';
import { api } from '../utils/api';

export const Settings: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');

  // Change password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const result = await api.changePassword(currentPassword, newPassword);
      setPasswordSuccess(result.message || 'Password changed. Please log in again.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleThemeChange = (newTheme: ThemeMode) => {
    setTheme(newTheme);

    // Show confirmation message with detailed description
    const themeLabel =
      newTheme === 'system' ? 'Follow device' : newTheme === 'light' ? 'Light' : 'Dark';
    const description =
      newTheme === 'system'
        ? 'The theme will now automatically follow your device settings'
        : `The ${themeLabel.toLowerCase()} theme has been applied to your interface`;
    setConfirmationMessage(`${themeLabel} theme selected. ${description}.`);

    // Clear confirmation message after 4 seconds
    setTimeout(() => {
      setConfirmationMessage('');
    }, 4000);
  };

  const handleReset = () => {
    setTheme('system');
    setConfirmationMessage(
      'Theme reset to Follow device. The theme will now automatically follow your device settings.'
    );

    // Clear confirmation message after 4 seconds
    setTimeout(() => {
      setConfirmationMessage('');
    }, 4000);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-base-100 shadow-sm rounded-lg">
        <div className="px-6 py-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-base-content mb-8">Settings</h1>

          <fieldset className="space-y-4" role="radiogroup" aria-labelledby="theme-legend">
            <legend
              id="theme-legend"
              data-testid="theme-legend"
              className="text-lg font-medium text-base-content mb-4"
            >
              Theme
            </legend>

            <div className="space-y-4 sm:space-y-3">
              <div className="flex items-start sm:items-center">
                <input
                  id="theme-system"
                  name="theme"
                  type="radio"
                  value="system"
                  checked={theme === 'system'}
                  onChange={(e) => handleThemeChange(e.target.value as ThemeMode)}
                  className="h-5 w-5 sm:h-4 sm:w-4 mt-0.5 sm:mt-0 text-brand-600 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 border-base-300 transition-all"
                  aria-describedby="theme-system-description"
                />
                <div className="ml-3 flex-1">
                  <label
                    htmlFor="theme-system"
                    className="block text-base sm:text-sm font-medium text-base-content cursor-pointer"
                  >
                    Follow device
                  </label>
                  <p id="theme-system-description" className="mt-1 text-sm text-base-content/70">
                    Automatically switch between light and dark themes based on your device settings
                  </p>
                </div>
              </div>

              <div className="flex items-start sm:items-center">
                <input
                  id="theme-light"
                  name="theme"
                  type="radio"
                  value="light"
                  checked={theme === 'light'}
                  onChange={(e) => handleThemeChange(e.target.value as ThemeMode)}
                  className="h-5 w-5 sm:h-4 sm:w-4 mt-0.5 sm:mt-0 text-brand-600 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 border-base-300 transition-all"
                  aria-describedby="theme-light-description"
                />
                <div className="ml-3 flex-1">
                  <label
                    htmlFor="theme-light"
                    className="block text-base sm:text-sm font-medium text-base-content cursor-pointer"
                  >
                    Light
                  </label>
                  <p id="theme-light-description" className="mt-1 text-sm text-base-content/70">
                    Use the light theme
                  </p>
                </div>
              </div>

              <div className="flex items-start sm:items-center">
                <input
                  id="theme-dark"
                  name="theme"
                  type="radio"
                  value="dark"
                  checked={theme === 'dark'}
                  onChange={(e) => handleThemeChange(e.target.value as ThemeMode)}
                  className="h-5 w-5 sm:h-4 sm:w-4 mt-0.5 sm:mt-0 text-brand-600 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 border-base-300 transition-all"
                  aria-describedby="theme-dark-description"
                />
                <div className="ml-3 flex-1">
                  <label
                    htmlFor="theme-dark"
                    className="block text-base sm:text-sm font-medium text-base-content cursor-pointer"
                  >
                    Dark
                  </label>
                  <p id="theme-dark-description" className="mt-1 text-sm text-base-content/70">
                    Use the dark theme
                  </p>
                </div>
              </div>
            </div>

            {/* Reset button */}
            <div className="pt-6">
              <button
                onClick={handleReset}
                type="button"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-3 sm:px-3 sm:py-2 border border-base-300 shadow-sm text-base sm:text-sm leading-5 sm:leading-4 font-medium rounded-md text-base-content bg-base-100 hover:bg-base-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-all min-h-[44px] sm:min-h-0"
                aria-label="Reset theme to default (Follow device)"
              >
                Reset to default
              </button>
            </div>

            {/* Confirmation message with aria-live for screen readers */}
            <div
              aria-live="polite"
              aria-atomic="true"
              className={`transition-opacity duration-300 ${
                confirmationMessage ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {confirmationMessage && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">{confirmationMessage}</p>
                </div>
              )}
            </div>
          </fieldset>

          {/* Change Password */}
          <div className="mt-10 pt-8 border-t border-base-300">
            <h2 className="text-lg font-medium text-base-content mb-4">Change Password</h2>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              {passwordError && <div className="alert alert-error text-sm">{passwordError}</div>}
              {passwordSuccess && (
                <div className="alert alert-success text-sm">{passwordSuccess}</div>
              )}

              <div className="form-control">
                <label className="label" htmlFor="currentPassword">
                  <span className="label-text">Current Password</span>
                </label>
                <input
                  id="currentPassword"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="input input-bordered input-sm w-full"
                />
              </div>

              <div className="form-control">
                <label className="label" htmlFor="newPassword">
                  <span className="label-text">New Password</span>
                </label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input input-bordered input-sm w-full"
                  minLength={8}
                />
                <label className="label">
                  <span className="label-text-alt text-base-content/50">
                    Min 8 chars, uppercase, lowercase, number, and special character
                  </span>
                </label>
              </div>

              <div className="form-control">
                <label className="label" htmlFor="confirmNewPassword">
                  <span className="label-text">Confirm New Password</span>
                </label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="input input-bordered input-sm w-full"
                  minLength={8}
                />
              </div>

              <button type="submit" disabled={passwordLoading} className="btn btn-primary btn-sm">
                {passwordLoading ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  'Change Password'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
